import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Episode, Project, EpisodeTranslationSummary } from '../../../types';
import { getEpisodeContent, updateEpisode, normalizeEpisode, resolvePronouns, getEpisodeTranslations, getEpisodeTranslation, getRefTranslationLanguages, startEpisodeTranslation } from '../../../api';
import Modal from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import EpisodeContentCard from './EpisodeContentCard';

interface EpisodeContentModalProps {
  /** 选中的分集（null = 不渲染）。 */
  episode: Episode | null;
  project: Project | null;
  /** 全部分集（用于弹窗内「上一集/下一集」按 index 顺序快速切换）。 */
  episodes?: Episode[];
  open: boolean;
  onClose: () => void;
  /** 切换到上一集/下一集（外层据此更新选中分集）。 */
  onNavigate?: (episode: Episode) => void;
  /** 跳转到当前集的视频创作页。 */
  onCreateVideo?: (episode: Episode) => void;
  /** 进入当前集的导演标注工作区。 */
  onDirectorAnnotations?: (episode: Episode) => void;
  /**
   * 保存原文 / 提交改写后回调（外部据此刷新分集列表）。
   * taskStarted=true 表示提交了一个异步任务（重新改写），外层应立即开启轮询，
   * 不能只 loadEpisodes 一次——后端状态可能还没体现在这次返回里，会漏掉「改写中」进度。
   */
  onChanged: (taskStarted?: boolean) => void;
}

/**
 * 把分集页底部的「剧集原文」面板（EpisodeContentCard）自包含成弹窗，供剧本信息页的
 * 全集分集状态卡片点击打开。内部持有内容加载 / 编辑保存 / 重新改写的全部状态与逻辑，
 * 功能与分集页一致；切换分集时按 episodeId 重置并重新加载，避免串内容。
 */
export default function EpisodeContentModal({ episode, project, episodes, open, onClose, onNavigate, onCreateVideo, onDirectorAnnotations, onChanged }: EpisodeContentModalProps) {
  const { t } = useTranslation('episodeDetail');
  const toast = useToast();
  const epId = episode?.id;

  const [content, setContent] = useState('');            // 代词标注版
  const [contentTab, setContentTab] = useState<string>('original');
  const [originalContent, setOriginalContent] = useState('');
  const [originalContentLoaded, setOriginalContentLoaded] = useState(false);
  const [normalizedContent, setNormalizedContent] = useState('');
  const [normalizedContentLoaded, setNormalizedContentLoaded] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [savingContent, setSavingContent] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [resolvingPronouns, setResolvingPronouns] = useState(false);

  // 参考翻译：summary 列表 + 各语言已加载内容（懒加载，点击 tab 时拉取）。
  const [translations, setTranslations] = useState<EpisodeTranslationSummary[]>([]);
  const [transContent, setTransContent] = useState<Record<string, string>>({});
  const [presetLangs, setPresetLangs] = useState<string[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const annotatedURL = episode?.annotatedContentURL;
  const normalizedURL = episode?.normalizedContentURL;

  const loadTranslations = useCallback(async () => {
    if (!epId) return;
    try {
      const r = await getEpisodeTranslations(epId);
      setTranslations(r.translations || []);
    } catch (err) { console.error('Failed to load episode translations:', err); }
  }, [epId]);

  // 打开 / 切换分集时重置翻译态并拉取一次 summary。
  useEffect(() => {
    if (!open || !epId) return;
    setTranslations([]); setTransContent({});
    loadTranslations();
  }, [open, epId, loadTranslations]);

  // 任一语言 translating 时每 4s 轮询；无 translating 或关闭则停；卸载清理。
  useEffect(() => {
    const hasTranslating = translations.some(tr => tr.status === 'translating');
    if (open && hasTranslating && !pollRef.current) {
      pollRef.current = setInterval(loadTranslations, 4000);
    }
    if ((!open || !hasTranslating) && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open, translations, loadTranslations]);

  const loadTranslationContent = useCallback(async (language: string) => {
    if (!epId || transContent[language] !== undefined) return;
    try {
      const d = await getEpisodeTranslation(epId, language);
      if (d.status === 'completed' && d.content) {
        setTransContent(prev => ({ ...prev, [language]: d.content as string }));
      }
    } catch (err) { console.error('Failed to load translation content:', err); }
  }, [epId, transContent]);

  // 打开弹窗时拉取一次预设参考语言列表（用于「开始翻译」下拉）。
  useEffect(() => {
    if (!open) return;
    getRefTranslationLanguages()
      .then(r => setPresetLangs(r.languages || []))
      .catch(err => console.error('Failed to load preset languages:', err));
  }, [open]);

  // 尚无翻译记录的预设语言（可手动发起翻译）。
  const startableLanguages = presetLangs.filter(l => !translations.some(tr => tr.language === l));

  // 手动发起某语言翻译：乐观插入 translating tab（触发轮询）并切换过去，再向服务端发起。
  const handleStartTranslation = useCallback(async (language: string) => {
    if (!epId || !language) return;
    setTranslations(prev => prev.some(t => t.language === language) ? prev : [...prev, { language, status: 'translating' }]);
    setContentTab(`trans:${language}`);
    try {
      await startEpisodeTranslation(epId, language);
      loadTranslations(); // 与服务端对齐（并经现有 effect 重启轮询）
    } catch (err) {
      console.error('Failed to start translation:', err);
      loadTranslations();
    }
  }, [epId, loadTranslations]);

  // 打开 / 切换分集时：重置全部内容态并加载默认内容（代词标注版 > 改写版 > 原文）。
  useEffect(() => {
    if (!open || !epId) return;
    setContent('');
    setOriginalContent(''); setOriginalContentLoaded(false);
    setNormalizedContent(''); setNormalizedContentLoaded(false);
    setEditingContent(false); setEditedContent('');
    setContentTab(annotatedURL ? 'annotated' : normalizedURL ? 'normalized' : 'original');

    let cancelled = false;
    (async () => {
      try {
        if (annotatedURL) {
          const d = await getEpisodeContent(epId, 'annotated');
          if (!cancelled) setContent(d.content);
        } else if (normalizedURL) {
          const d = await getEpisodeContent(epId, 'normalized');
          if (!cancelled) { setNormalizedContent(d.content); setNormalizedContentLoaded(true); }
        } else {
          const d = await getEpisodeContent(epId);
          if (!cancelled) { setOriginalContent(d.content); setOriginalContentLoaded(true); }
        }
      } catch (err) {
        console.error('Failed to load episode content:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, epId, annotatedURL, normalizedURL]);

  const loadOriginal = useCallback(async () => {
    if (!epId || originalContentLoaded) return;
    try {
      const d = await getEpisodeContent(epId);
      setOriginalContent(d.content); setOriginalContentLoaded(true);
    } catch (err) { console.error('Failed to load original content:', err); }
  }, [epId, originalContentLoaded]);

  const loadNormalized = useCallback(async () => {
    if (!epId || normalizedContentLoaded) return;
    try {
      const d = await getEpisodeContent(epId, 'normalized');
      setNormalizedContent(d.content); setNormalizedContentLoaded(true);
    } catch (err) { console.error('Failed to load normalized content:', err); }
  }, [epId, normalizedContentLoaded]);

  const handleSave = async () => {
    if (!epId) return;
    setSavingContent(true);
    try {
      await updateEpisode(epId, editedContent);
      setContent(editedContent);
      setOriginalContent(editedContent);
      setEditingContent(false);
      toast.success(t('content.scriptSaved'));
      onChanged();
    } catch (err) {
      console.error('Failed to save content:', err);
      toast.error(t('content.saveFailed'));
    } finally {
      setSavingContent(false);
    }
  };

  const handleNormalize = async () => {
    if (!epId) return;
    setNormalizing(true);
    try {
      await normalizeEpisode(epId);
      toast.success(t('normalizePrompt.normalizeSubmitted'));
      setNormalizedContentLoaded(false);
      onChanged(true); // 改写是异步任务：让外层立即开启轮询
      onClose(); // 关闭弹窗，回到列表看改写进度
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('normalizePrompt.normalizeFailed'));
    } finally {
      setNormalizing(false);
    }
  };

  const handleResolvePronouns = async () => {
    if (!epId) return;
    setResolvingPronouns(true);
    try {
      await resolvePronouns(epId);
      toast.success(t('normalizePrompt.resolveSubmitted'));
      onChanged(true);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('normalizePrompt.resolveFailed'));
    } finally {
      setResolvingPronouns(false);
    }
  };

  if (!episode) return null;

  // 按 index 顺序的上一集 / 下一集（用于弹窗右上角快捷切换，逐集浏览原文处理结果）。
  const sortedEps = episodes ? [...episodes].sort((a, b) => a.index - b.index) : [];
  const curIdx = sortedEps.findIndex(ep => ep.id === episode.id);
  const prevEp = curIdx > 0 ? sortedEps[curIdx - 1] : null;
  const nextEp = curIdx >= 0 && curIdx < sortedEps.length - 1 ? sortedEps[curIdx + 1] : null;
  const showEpisodeNav = !!onNavigate && sortedEps.length > 1;
  const navBtnStyle = (enabled: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
    fontSize: '0.8rem', fontWeight: 600, borderRadius: '6px', border: '1px solid var(--border)',
    background: 'var(--surface)', color: enabled ? 'var(--text-1)' : 'var(--text-3)',
    cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5, whiteSpace: 'nowrap',
  });
  const createBtnStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
    fontSize: '0.8rem', fontWeight: 600, borderRadius: '6px', border: '1px solid var(--theme-storyboard)',
    background: 'var(--theme-storyboard)', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
  };
  const annotationReady = !!episode.annotatedContentURL;
  const annotationBtnStyle: React.CSSProperties = {
    ...createBtnStyle,
    borderColor: annotationReady ? 'var(--primary)' : 'var(--border)',
    background: annotationReady ? 'var(--primary)' : 'var(--surface-2)',
    color: annotationReady ? '#fff' : 'var(--text-3)',
    cursor: annotationReady ? 'pointer' : 'not-allowed',
    opacity: annotationReady ? 1 : 0.65,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`EP${episode.index}`}
      titleExtra={showEpisodeNav ? (
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="button" disabled={!prevEp} onClick={() => prevEp && onNavigate?.(prevEp)} style={navBtnStyle(!!prevEp)}>
            {t('nav.prevEpisode')}
          </button>
          <button type="button" disabled={!nextEp} onClick={() => nextEp && onNavigate?.(nextEp)} style={navBtnStyle(!!nextEp)}>
            {t('nav.nextEpisode')}
          </button>
        </div>
      ) : undefined}
      width="80vw"
      contentStyle={{ maxWidth: 'none', height: '80vh', maxHeight: 'none' }}
      bodyStyle={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <EpisodeContentCard
        fillHeight
        headerExtra={(onCreateVideo || onDirectorAnnotations) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '0.5rem' }}>
            {onDirectorAnnotations && (
              <>
                <button
                  type="button"
                  disabled={!annotationReady}
                  onClick={() => annotationReady && onDirectorAnnotations(episode)}
                  style={annotationBtnStyle}
                >
                  {t('content.directorAnnotations')}
                </button>
                {!annotationReady && (
                  <span style={{ color: 'var(--warning)', fontSize: '0.75rem' }}>{t('content.directorAnnotationsPrerequisite')}</span>
                )}
              </>
            )}
            {onCreateVideo && (
              <button type="button" onClick={() => onCreateVideo(episode)} style={createBtnStyle}>
                {t('content.goCreate')}
              </button>
            )}
          </div>
        )}
        episode={episode}
        projectData={project}
        content={content}
        contentTab={contentTab}
        originalContent={originalContent}
        originalContentLoaded={originalContentLoaded}
        normalizedContent={normalizedContent}
        normalizedContentLoaded={normalizedContentLoaded}
        translations={translations}
        transContent={transContent}
        startableLanguages={startableLanguages}
        onStartTranslation={handleStartTranslation}
        editingContent={editingContent}
        editedContent={editedContent}
        savingContent={savingContent}
        normalizingEpisode={normalizing}
        resolvingPronouns={resolvingPronouns}
        t={t}
        onContentTabChange={(tab) => {
          setContentTab(tab);
          if (tab === 'original') loadOriginal();
          if (tab === 'normalized') loadNormalized();
          if (tab.startsWith('trans:')) loadTranslationContent(tab.slice('trans:'.length));
        }}
        onStartEdit={() => { setEditedContent(originalContentLoaded ? originalContent : content); setEditingContent(true); loadOriginal(); }}
        onCancelEdit={() => setEditingContent(false)}
        onEditedContentChange={setEditedContent}
        onSaveContent={handleSave}
        onNormalize={handleNormalize}
        onResolvePronouns={handleResolvePronouns}
      />
    </Modal>
  );
}
